import { QueryClient } from '@tanstack/react-query';
import { getHttpErrorStatus, isAuthError } from '@/utils/auth/isAuthError';
import { backoffWithJitter, RETRY_MAX_ATTEMPTS } from '@/utils/retry/backoff';

let queryClient: QueryClient;

/**
 * Decides whether a failed query should be retried.
 * - Auth errors (401 / JWT-expired) are never retried — a single controlled token refresh
 *   is handled elsewhere; retrying an expired token would loop with no recovery.
 * - 404s are not retried (the resource does not exist).
 * - Every other error (incl. opaque/CORS/network failures with no readable status) is capped
 *   at RETRY_MAX_ATTEMPTS so no failure type can produce a request storm.
 */
export const shouldRetryQuery = (failureCount: number, error: unknown): boolean => {
  if (isAuthError(error)) {
    return false;
  }
  if (getHttpErrorStatus(error) === 404) {
    return false;
  }
  return failureCount < RETRY_MAX_ATTEMPTS;
};

export function getQueryClientInstance(): QueryClient {
  if (!queryClient) {
    queryClient = new QueryClient({
      defaultOptions: {
        queries: {
          staleTime: 1_000 * 60 * 60 * 1, // 1 hour until refresh from server
          gcTime: Infinity,
          retry: shouldRetryQuery,
          retryDelay: backoffWithJitter,
        },
      },
    });
  }
  return queryClient;
}
